import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  InputAdornment,
  List,
  ListItem,
  ListItemText,
  TextField,
  Typography,
} from '@mui/material';
import { Search as SearchIcon } from '@mui/icons-material';
import { apiCall } from '../../utils/fetchAPI';
import { listingMeta } from '../../constants/directory';

/** Below this the API refuses the search anyway; it is "not yet", not an error. */
const MIN_LENGTH = 2;
const DELAY_MS = 300;

/**
 * "Votre activité est-elle déjà sur le site ?" — the directory searched by
 * name, so somebody whose shop an admin already listed finds it before
 * writing a duplicate.
 *
 * Listings somebody already maintains are shown too, marked as such: hiding
 * them would leave a professional who finds no match wondering whether to
 * search again, when the answer is that their business is in somebody else's
 * hands — something to take up with the hub, not to work around.
 */
function ClaimSearch({ onClaim }) {
  const [term, setTerm] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [searched, setSearched] = useState(false);

  useEffect(() => {
    const query = term.trim();

    if (query.length < MIN_LENGTH) {
      setResults([]);
      setSearched(false);
      return undefined;
    }

    let cancelled = false;

    const timer = setTimeout(async () => {
      setSearching(true);

      try {
        const response = await apiCall(`/providers?q=${encodeURIComponent(query)}&limit=8`, { method: 'GET' });
        const data = await response.json().catch(() => ({}));

        if (!cancelled) {
          setResults(response.ok ? data.data || [] : []);
          setSearched(true);
        }
      } catch {
        if (!cancelled) {
          setResults([]);
          setSearched(true);
        }
      } finally {
        if (!cancelled) {
          setSearching(false);
        }
      }
    }, DELAY_MS);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [term]);

  return (
    <Box>
      <TextField
        label="Le nom de votre activité"
        value={term}
        onChange={(e) => setTerm(e.target.value)}
        fullWidth
        autoFocus
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon />
            </InputAdornment>
          ),
          endAdornment: searching ? (
            <InputAdornment position="end">
              <CircularProgress size={18} aria-label="Recherche en cours" />
            </InputAdornment>
          ) : null,
        }}
      />

      {searched && 0 === results.length && (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
          Aucune fiche à ce nom dans l’annuaire.
        </Typography>
      )}

      {results.length > 0 && (
        <List disablePadding sx={{ mt: 1 }}>
          {results.map((provider, index) => (
            <ListItem
              key={provider.uuid}
              divider={index < results.length - 1}
              disableGutters
              secondaryAction={
                provider.claimable ? (
                  <Button variant="outlined" size="small" onClick={() => onClaim(provider)}>
                    C’est la mienne
                  </Button>
                ) : (
                  <Typography variant="caption" color="text.secondary">
                    Déjà gérée par un compte
                  </Typography>
                )
              }
              sx={{ pr: 18 }}
            >
              <ListItemText
                primary={provider.name}
                secondary={listingMeta(provider.category, provider.neighborhood)}
              />
            </ListItem>
          ))}
        </List>
      )}
    </Box>
  );
}

export default ClaimSearch;
